import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import { HttpRequestError } from "client/Api/httpClient";
import { DocumentItemType, DocumentsRecycleBinItem } from "client/Api/Documents/SharePoint/common";
import { SharePointEvent, publish } from "client_legacy/Documents/SharePoint/documentEvents";
import {
	recoverRecycledItems,
	RecoverFailedResponse,
	RestoredDocumentModel
} from "client/Api/Documents/SharePoint/sharePointRecycleBin";
import {
	BusyIndicator,
	FormattedText,
	Portal,
	useAlertDialog
} from "client/Components";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { AlertManager } from "client/Hooks";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export function useRecoverFromRecyclebin(
	onDeleted: (ids: readonly Guid[]) => void,
	alertManager: AlertManager
): [
	element: ReactElement | null,
	invoke: (documents: readonly DocumentsRecycleBinItem[]) => Promise<void>
] {
	const [dialog, openDocumentCantBeRecoveredDialog] = useDocumentCantBeRecoveredDialog();
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const invoke = useCallback(
		async (documents: readonly DocumentsRecycleBinItem[]): Promise<void> => {
			setIsBusy(true);
			try {
				const recycleBinIds: Guid[] = documents.map(x => x.guid);
				const recoveredDocuments: RestoredDocumentModel[] = await recoverRecycledItems({
					location: convertToLocationModel(
						documents[0].location
					),
					recycleBinIds
				});

				onDeleted(recycleBinIds);

				recoveredDocuments.forEach((x) => publish(SharePointEvent.AddedOrReplaced, x.document));

				alertManager.showSuccess(localize("_FilesRecoveredSuccessfully"));
			} catch (e) {
				let handled: boolean = false;
				if (e instanceof HttpRequestError && e.statusCode === 400) {
					try {
						const response: RecoverFailedResponse = e.readJson<RecoverFailedResponse>();
						const recoveredGuids: Guid[] = [];
						for (const model of response.recoveredDocuments) {
							publish(SharePointEvent.AddedOrReplaced, model.document);
							recoveredGuids.push(model.recycleBinId);
						}

						if (recoveredGuids.length > 0) {
							onDeleted(recoveredGuids);
						}

						await openDocumentCantBeRecoveredDialog(
							documents,
							response
						);

						handled = true;
					} catch (e) {
						console.error(e);
					}
				}

				if (!handled) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			} finally {
				setIsBusy(false);
			}
		},
		[
			alertManager,
			onDeleted,
			openDocumentCantBeRecoveredDialog,
			setIsBusy
		]
	);

	const renderElement = (): ReactElement | null => {
		if (!dialog && !isBusy) {
			return null;
		}

		return (
			<>
				{isBusy && (
					<Portal container={document.body}>
						<BusyIndicator />
					</Portal>
				)}

				{dialog}
			</>
		);
	};

	return [renderElement(), invoke];
}

function useDocumentCantBeRecoveredDialog(): [
	element: ReactElement | null,
	open: (
		documents: readonly DocumentsRecycleBinItem[],
		response: RecoverFailedResponse
	) => Promise<void>
] {
	const [alertDialog, openAlertDialog] = useAlertDialog();

	const open = useCallback(
		async (
			documents: readonly DocumentsRecycleBinItem[],
			response: RecoverFailedResponse
		): Promise<void> => {
			let dialogTitle: string;
			let dialogBody: ReactElement;

			const { problemDocuments } = response;

			if (problemDocuments.length === 1
				&& problemDocuments[0].reason === "NameAlreadyExist"
			) {
				const problemId: Guid = problemDocuments[0]
					.recycleBinId;

				const recycleBinItem: DocumentsRecycleBinItem | undefined = documents.find(x => x.guid === problemId);

				if (!recycleBinItem) {
					throw new Error("Document with problem not found in collection");
				}

				const [titleKey, bodyKey]: [LocalizationKey, LocalizationKey] = recycleBinItem.itemType === DocumentItemType.Folder
					? ["_FolderAlreadyExist", "_ToRestoreFolderRenamePreviousOne"]
					: ["_FileAlreadyExist", "_ToRestoreFileRenamePreviousOne"];

				dialogTitle = localize(titleKey);
				dialogBody = (
					<FormattedText
						text={localize(bodyKey)}
						thingsToReplace={[
							{ what: "{name}", filler: recycleBinItem.name }
						]}
					/>
				);
			} else {
				dialogTitle = localize("_UnableToRestoreDocuments");
				dialogBody = (
					<>
						{localize("_SomeDocumentsAlreadyExist")}
					</>
				);
			}

			await openAlertDialog({
				title: dialogTitle,
				children: dialogBody,
				confirmText: localize("_Close"),
				trackingId: "sp-unable-to-restore-document-dialog"
			});
		},
		[openAlertDialog]
	);

	return [alertDialog, open];
}
